import { useState } from 'react'
import { users as api } from '@/api/endpoints'
import type { Team } from '@/api/types'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DASH } from '@/ui/format'
import { useSession } from '@/hooks/useSession'
import { ACTION, ACTION_DANGER, ACTION_PLAIN } from '@/views/vm/chips'
import { QuotaPanel } from './QuotaPanel'
import { RolePanel } from './RolePanel'
import { TeamPanel } from './TeamPanel'
import type { Candidate } from './teamPick'

// 사용자 목록 제목 줄의 조작(F-58) — 대상은 체크한 사람들이다. 최고 관리자만 본다.
// **조작은 늘 보인다**(F-60): 고르기 전에도 무엇을 할 수 있는 화면인지 알아야 하므로 넷이 제자리에 서고 할 수 없는 것이 잠긴다.
// 탈퇴는 체크한 사람들 모두가 대상이고(F-80) 자기 자신이 섞이면 잠긴다 — 목록을 보이고 묻는다.
// **`권한`·`팀 관리`·`직위 관리` 는 잠기지 않는다**(F-66·F-72·F-81) — 대상을 그 창 안에서 고를 수 있으므로 아무도 고르지 않은 상태도 할 수 있는 상태다.
// 권한 창에 자기 자신은 없다 — 자기 등급은 내 정보에서 내려놓기만 한다. 최고 관리자끼리는 서로 바꾼다(F-79).
// 팀은 등급을 가르지 않는다: 소속은 권한이 아니라 공유와 쿼터의 단위다.

type Open = { kind: 'role' } | { kind: 'withdraw' } | { kind: 'team' } | { kind: 'quota' } | null

export function UserActions({ chosen, teams, onDone }: { chosen: Candidate[]; teams: Team[]; onDone: () => void }) {
  const [open, setOpen] = useState<Open>(null)
  const me = useSession()
  if (me.role !== 'superadmin') return null

  const leaving = chosen.length > 0 && chosen.every((c) => c.user.id !== me.id) ? chosen : null
  const close = () => setOpen(null)
  // 여럿을 한 창에서 다루는 셋은 두 칸이라 넓다.
  const wide = open?.kind === 'team' || open?.kind === 'quota' || open?.kind === 'role'
  const done = () => {
    close()
    onDone()
  }

  return (
    <>
      <div className="flex items-center gap-1.5">
        <button type="button" className={`${ACTION} ${ACTION_PLAIN}`} onClick={() => setOpen({ kind: 'role' })}>
          권한
        </button>
        <button type="button" className={`${ACTION} ${ACTION_PLAIN}`} onClick={() => setOpen({ kind: 'team' })}>
          팀 관리
        </button>
        <button type="button" className={`${ACTION} ${ACTION_PLAIN}`} onClick={() => setOpen({ kind: 'quota' })}>
          직위 관리
        </button>
        <button type="button" className={`${ACTION} ${ACTION_DANGER}`} disabled={!leaving} onClick={() => setOpen({ kind: 'withdraw' })}>
          탈퇴
        </button>
      </div>
      <Dialog open={open !== null} onOpenChange={(v) => (v ? undefined : close())}>
        <DialogContent aria-describedby={undefined} className={wide ? 'sm:max-w-3xl' : undefined}>
          {open?.kind === 'role' ? (
            <RolePanel seed={chosen.filter((c) => c.user.id !== me.id)} teams={teams} onDone={done} />
          ) : null}
          {open?.kind === 'withdraw' && leaving ? <WithdrawForm people={leaving} onClose={done} /> : null}
          {open?.kind === 'team' ? (
            <TeamPanel seed={chosen} teams={teams} onDone={done} />
          ) : null}
          {open?.kind === 'quota' ? <QuotaPanel seed={chosen} teams={teams} onDone={done} onApplied={onDone} /> : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

function WithdrawForm({ people, onClose }: { people: Candidate[]; onClose: () => void }) {
  // 실패한 사람만 남는다 — 같은 버튼이 곧 재시도다.
  const [left, setLeft] = useState<string[]>(() => people.map((p) => p.user.id))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const targets = people.filter((p) => left.includes(p.user.id))
  const withdraw = async () => {
    setBusy(true)
    setError(null)
    let first: unknown = null
    const failed = new Set<string>()
    for (const p of targets) {
      try {
        await api.withdraw(p.user.id)
      } catch (e) {
        if (first === null) first = e
        failed.add(p.user.id)
      }
    }
    setBusy(false)
    if (first !== null) {
      setError(first)
      setLeft((ids) => ids.filter((id) => failed.has(id)))
      return
    }
    onClose()
  }
  return (
    <>
      <DialogHeader>
        <DialogTitle>탈퇴 처리</DialogTitle>
      </DialogHeader>
      <ul aria-label="탈퇴할 사용자" className="max-h-60 overflow-y-auto rounded-md border">
        {targets.map((p) => (
          <li key={p.user.id} className="flex items-center gap-2 px-3 py-1.5 text-[13px]">
            <span className="truncate font-medium">{p.user.username}</span>
            <span className="ml-auto min-w-0 truncate text-muted-foreground">{p.user.name ?? DASH}</span>
          </li>
        ))}
      </ul>
      <p className="text-[13px]">계정과 세션·키·VM 을 정리합니다. VM 대여 기록은 보존됩니다.</p>
      <p className="text-[13px] font-medium">{targets.length}명을 탈퇴 처리하시겠습니까?</p>
      <Err error={error} />
      <DialogFooter>
        <Button variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button variant="destructive" disabled={busy || targets.length === 0} onClick={() => void withdraw()}>
          {busy ? '처리 중' : '탈퇴 처리'}
        </Button>
      </DialogFooter>
    </>
  )
}
